import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  useGetPlaylistsQuery,
  useCreatePlaylistMutation,
  useAddSongToPlaylistMutation,
} from "../../../state/playlistApi";
import { trackUIEvent } from "../../../utils/analytics";
import { useToastContext } from "../../../contexts/ToastContext";
import themes from "../../shared/themes";
import ThemeDropdown from "../../shared/ThemeDropdown";
import { CloseIcon } from "../../shared/Icons";
import "./styles.css";

/**
 * AddToPlaylistModal component - Modal for adding songs to playlists
 * Handles both adding to existing playlists and creating new ones with theme selection
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to close the modal
 * @param {Object} props.song - Song object to add to playlist
 * @returns {JSX.Element} Add to playlist modal component
 */
export default function AddToPlaylistModal({ isOpen, onClose, song }) {
  const {
    isAuthenticated,
    loginWithPopup,
    isLoading: authLoading,
  } = useAuth0();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [selectedTheme, setSelectedTheme] = useState("Faith");
  const [showSavedMessage, setShowSavedMessage] = useState(false);

  const { data: playlists = [], isLoading: playlistsLoading } =
    useGetPlaylistsQuery(undefined, {
      skip: !isAuthenticated,
    });

  const [createPlaylist, { isLoading: creatingPlaylist }] =
    useCreatePlaylistMutation();
  // eslint-disable-next-line
  const [addSongToPlaylist, { isLoading: addingSong }] =
    useAddSongToPlaylistMutation();
  const { error: showError, success: showSuccess } = useToastContext();

  const handleAddToExistingPlaylist = async (playlistId) => {
    try {
      await addSongToPlaylist({
        playlistId,
        songId: song._id,
      }).unwrap();

      trackUIEvent("Add to Playlist", "Added to existing playlist", {
        songTitle: song.title,
        playlistId,
      });

      // Show success toast and saved message briefly
      showSuccess(`Added "${song.title}" to playlist`);
      setShowSavedMessage(true);
      setTimeout(() => {
        setShowSavedMessage(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Failed to add song to playlist:", error);
      showError("Failed to add song to playlist. Please try again.");
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newPlaylistName.trim()) {
      showError("Please enter a playlist name");
      return;
    }

    try {
      // Create new playlist with selected theme and the current song
      // eslint-disable-next-line
      const newPlaylist = await createPlaylist({
        name: newPlaylistName.trim(),
        theme: selectedTheme,
        songs: [song._id],
      }).unwrap();

      trackUIEvent("Add to Playlist", "Created new playlist", {
        songTitle: song.title,
        playlistName: newPlaylistName,
      });

      // Show success toast and saved message briefly
      showSuccess(
        `Created playlist "${newPlaylistName}" and added "${song.title}"`
      );
      setShowSavedMessage(true);
      setTimeout(() => {
        setShowSavedMessage(false);
        onClose();
        setNewPlaylistName("");
        setShowCreateForm(false);
      }, 1500);
    } catch (error) {
      console.error("Failed to create playlist:", error);
      showError("Failed to create playlist. Please try again.");
    }
  };

  const handleClose = () => {
    onClose();
    setShowCreateForm(false);
    setNewPlaylistName("");
    setSelectedTheme("Faith");
  };

  const handleLogin = async () => {
    try {
      await loginWithPopup({
        authorizationParams: {
          audience: process.env.REACT_APP_AUDIENCE,
        },
      });
      trackUIEvent("Add to Playlist", "Login via popup");
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  if (!isOpen || !song) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="add-to-playlist-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Add "{song.title}" to Playlist</h3>
          <button className="close-button" onClick={handleClose}>
            <CloseIcon size={20} />
          </button>
        </div>

        {showSavedMessage && (
          <div className="saved-message">
            <span>Saved!</span>
          </div>
        )}

        <div className="modal-content">
          {!isAuthenticated ? (
            <div className="auth-required">
              <div className="login-prompt">
                <h4>Login Required</h4>
                <p>Please log in to add "{song.title}" to your playlists.</p>
                <button
                  className="login-button"
                  onClick={handleLogin}
                  disabled={authLoading}
                >
                  {authLoading ? "Logging in..." : "Login"}
                </button>
              </div>
            </div>
          ) : (
            <>
              {!showCreateForm ? (
                <div className="existing-playlists">
                  <div className="playlist-list">
                    {playlistsLoading ? (
                      <p>Loading playlists...</p>
                    ) : playlists.length === 0 ? (
                      <p className="no-playlists">
                        You don't have any playlists yet.
                      </p>
                    ) : (
                      playlists.map((playlist) => {
                        const theme = themes[playlist.theme] || themes.Faith;
                        return (
                          <div
                            key={playlist._id}
                            className="playlist-item"
                            onClick={() =>
                              handleAddToExistingPlaylist(playlist._id)
                            }
                            style={{
                              background: theme.gradient,
                            }}
                          >
                            <div className="playlist-left">
                              <span className="playlist-icon">
                                {theme.icon?.startsWith("/") ? (
                                  <img
                                    src={theme.icon}
                                    alt=""
                                    style={{ width: "24px", height: "24px" }}
                                  />
                                ) : (
                                  theme.icon
                                )}
                              </span>
                              <h4>{playlist.name}</h4>
                            </div>
                            <p>{playlist.songs?.length || 0} songs</p>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="modal-actions">
                    <button
                      className="create-new-btn"
                      onClick={() => setShowCreateForm(true)}
                    >
                      Create New Playlist
                    </button>
                  </div>
                </div>
              ) : (
                <div className="create-playlist-form">
                  <div className="form-group">
                    <label htmlFor="playlist-name">Playlist Name</label>
                    <input
                      id="playlist-name"
                      type="text"
                      value={newPlaylistName}
                      onChange={(e) => setNewPlaylistName(e.target.value)}
                      placeholder="Enter playlist name..."
                      maxLength={20}
                    />
                    <div className="char-counter">
                      {newPlaylistName.length}/20 characters
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="playlist-theme">Theme</label>
                    <ThemeDropdown
                      theme={selectedTheme}
                      onSelect={setSelectedTheme}
                    />
                  </div>

                  <div className="modal-actions">
                    <button
                      className="cancel-btn"
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="create-btn"
                      onClick={handleCreateAndAdd}
                      disabled={creatingPlaylist || !newPlaylistName.trim()}
                    >
                      {creatingPlaylist ? "Creating..." : "Create & Add"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
